import { memo, useMemo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import { Image as ImageIcon } from 'lucide-react'
import { cn } from '@/lib/cn'
import { remarkCitations } from '@/lib/remarkCitations'
import { rehypeWordReveal } from '@/lib/rehypeWordReveal'
import { splitMarkdownBlocks } from '@/lib/streamingMarkdown'
import { CitationChip } from './CitationChip'
import { CodeBlock } from './CodeBlock'
import { DiagramStreamingContext } from './diagramContext'

const linkProps = { target: '_blank', rel: 'noopener noreferrer' } as const

/*
 * Safety: raw HTML is skipped (no rehype-raw) and images are rendered as plain
 * links, never fetched. react-markdown's default urlTransform strips
 * javascript:/data: URLs.
 */
const components: Components = {
  a: ({ node: _node, children, ...props }) => (
    <a {...props} {...linkProps}>
      {children}
    </a>
  ),
  img: ({ node: _node, src, alt }) =>
    src ? (
      <a href={String(src)} {...linkProps} className="inline-flex items-center gap-1">
        <ImageIcon className="size-3.5" aria-hidden />
        {alt || 'Image'}
      </a>
    ) : (
      <span>{alt}</span>
    ),
  table: ({ node: _node, ...props }) => (
    <div className="table-wrap my-5 overflow-x-auto rounded-2xl bg-surface shadow-card">
      <table {...props} />
    </div>
  ),
  pre: CodeBlock,
  cite: ({ node, children }) => {
    const id = node?.properties?.dataSourceId
    if (typeof id === 'string') return <CitationChip id={id} fallback={children} />
    return <cite>{children}</cite>
  },
}

const withCitations = [remarkGfm, remarkCitations]
const withoutCitations = [remarkGfm]
const rehypePlugins = [rehypeHighlight]
const streamingRehypePlugins = [rehypeHighlight, rehypeWordReveal]

type MarkdownProps = {
  content: string
  /** Turn [S<n>] markers into chips (needs a CitationContext). */
  citations?: boolean
  compact?: boolean
  className?: string
  /** Fade in newly streamed words. */
  streaming?: boolean
  /**
   * Render top-level blocks separately so only the changed (last) block re-renders
   * as an answer streams. Used for chat answers, both while streaming and after,
   * so the layout doesn't shift when the stream ends.
   */
  incremental?: boolean
}

export const Markdown = memo(function Markdown({
  content,
  citations = true,
  compact,
  className,
  streaming = false,
  incremental = false,
}: MarkdownProps) {
  const blocks = useMemo(() => (incremental ? splitMarkdownBlocks(content) : null), [incremental, content])
  return (
    <div className={cn('prose-chat', compact && 'prose-compact', className)}>
      {blocks ? (
        blocks.map((block, index) => (
          <MarkdownBlock
            key={index}
            content={block}
            citations={citations}
            // Only the block being written gets the word fade-in.
            streaming={streaming && index === blocks.length - 1}
          />
        ))
      ) : (
        <MarkdownBody content={content} citations={citations} streaming={streaming} />
      )}
    </div>
  )
})

type BodyProps = { content: string; citations: boolean; streaming: boolean }

function MarkdownBody({ content, citations, streaming }: BodyProps) {
  return (
    // A diagram in the block still being written has incomplete source: it waits to be drawn.
    <DiagramStreamingContext.Provider value={streaming}>
      <ReactMarkdown
        remarkPlugins={citations ? withCitations : withoutCitations}
        rehypePlugins={streaming ? streamingRehypePlugins : rehypePlugins}
        components={components}
        skipHtml
      >
        {content}
      </ReactMarkdown>
    </DiagramStreamingContext.Provider>
  )
}

/** One top-level block; `display: contents` keeps the prose spacing rules intact. */
const MarkdownBlock = memo(function MarkdownBlock(props: BodyProps) {
  return (
    <div className="md-block contents">
      <MarkdownBody {...props} />
    </div>
  )
})
